import * as React from "react";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ChevronsUpDown, Download } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TableSearch, useRowSearch } from "@/components/table-search";
import { ColumnFilterButton, type DataTableColumnFilter } from "@/components/column-filter";

// The one table every Phase-4 list page renders through. Purely
// presentational/callback-driven — it never fetches, sorts, or paginates
// data itself; that orchestration is each page's TanStack Query call, wired
// through lib/table-state.ts's useTableState hook.

export interface DataTableColumn<T> {
  key: string;
  header: string;
  sortable?: boolean;
  cell: (row: T) => React.ReactNode;
  /** e.g. "text-right" for numeric columns. Applied to both the header `<th>` and the body `<td>`. */
  className?: string;
  /** Inline style for the header `<th>` only — paired with `cellStyle` so a column can tint its
   * header more strongly than its body (this table's existing tinted-column depth convention:
   * stronger header, lighter body). Exists specifically for a color derived at runtime from a
   * design token (e.g. `STATUS_DOT_COLOR`): Tailwind's scanner only ever picks up literal
   * `bg-[#xxxxxx]` strings actually present in source, so a color computed from a shared palette
   * constant has to reach the DOM via `style`, not a dynamically-built class name. */
  headerStyle?: React.CSSProperties;
  /** Inline style for the body `<td>` only — see `headerStyle`. */
  cellStyle?: React.CSSProperties;
  /** A funnel icon in the header opening this column's filter popover — see `columnFilter()`. */
  filter?: DataTableColumnFilter;
}

export interface DataTableCsvExport {
  onExport: () => void;
  disabled?: boolean;
}

export interface DataTableGroupHeaderCell {
  label: string;
  /** How many `columns` entries this group label spans — an empty-label entry with colSpan N leaves that range ungrouped. */
  colSpan: number;
  /** e.g. a background tint via a literal Tailwind class. */
  className?: string;
  /** Inline style — see `DataTableColumn.headerStyle`'s doc comment for why a token-derived color
   * (e.g. `STATUS_DOT_COLOR`) needs `style` rather than a class name. */
  style?: React.CSSProperties;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  sortBy?: string | null;
  sortOrder?: "asc" | "desc";
  loading?: boolean;
  error?: string | null;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /** Omit for an unsortable table. */
  onSortChange?: (columnKey: string) => void;
  /** Rendered above the table, e.g. filter inputs/selects a page composes itself. */
  filterBar?: React.ReactNode;
  /** Renders an export button when present; omit for tables the spec doesn't require it on. */
  csvExport?: DataTableCsvExport;
  /**
   * Optional spanning row rendered above the column-header row, for grouping
   * related columns under one label (e.g. "Merchant Ledger" over
   * Available/Pending/Frozen) — entries' `colSpan`s must sum to
   * `columns.length`; use an empty `label` to leave a range ungrouped.
   */
  groupHeaders?: DataTableGroupHeaderCell[];
  /**
   * When present, clicking anywhere on a row invokes this instead of the row
   * having no interaction — cursor becomes a pointer and rows get a hover
   * tint. A cell that has its own interactive element (a copy button, an
   * inline link) should stop propagation in its own `onClick` so it doesn't
   * also fire the row click.
   */
  onRowClick?: (row: T) => void;
  /** React list key, and avoids assuming every T has an `id` field. */
  getRowKey: (row: T) => string;
  emptyMessage?: string;
  /**
   * Client-side search box, left of `filterBar`: hides loaded rows (the current
   * page on a server-paginated list) that don't contain the query in any value.
   * Default on; pass `false` where the page already has its own search input.
   */
  searchable?: boolean;
}

/** No existing page-size-selector precedent in this app; 10/20/50 covers the spec's "at least" ask. */
const PAGE_SIZE_OPTIONS = [10, 20, 50];

const DEFAULT_EMPTY_MESSAGE = "No results match your filters. Try widening or clearing them.";

export function DataTable<T>({
  columns,
  items,
  total,
  page,
  pageSize,
  sortBy = null,
  sortOrder = "asc",
  loading = false,
  error = null,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  filterBar,
  csvExport,
  groupHeaders,
  onRowClick,
  getRowKey,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  searchable = true,
}: DataTableProps<T>) {
  const [query, setQuery] = React.useState("");
  const searching = searchable && query.trim() !== "";
  const visibleItems = useRowSearch(items, searchable ? query : "");
  // Per-instance id: a page's own `DataTable` and a row-detail dialog's
  // nested `DataTable` (e.g. the Ledger list + its ledger-transactions
  // dialog) can be mounted simultaneously — a hardcoded id would collide
  // and make the dialog's label focus the background page's hidden select.
  const pageSizeId = React.useId();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const showSkeleton = loading;
  const showError = !loading && !!error;
  const showEmpty = !loading && !error && visibleItems.length === 0;
  const showRows = !loading && !error && visibleItems.length > 0;

  return (
    <div className="space-y-3">
      {(searchable || filterBar || csvExport) && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            {searchable && <TableSearch value={query} onChange={setQuery} />}
            {searching && !loading && (
              <span className="text-xs text-muted-foreground">
                {visibleItems.length} of {items.length} shown
              </span>
            )}
            {filterBar}
          </div>
          {csvExport && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={csvExport.onExport}
              disabled={csvExport.disabled}
            >
              <Download />
              Export CSV
            </Button>
          )}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border bg-card">
        <Table>
          <TableHeader>
            {groupHeaders && (
              <TableRow className="hover:bg-transparent">
                {groupHeaders.map((group, index) => (
                  <TableHead
                    key={index}
                    colSpan={group.colSpan}
                    // Overrides TableHead's own h-10/font-medium defaults —
                    // this banner row is deliberately more compact and
                    // bolder than the column-header row right below it,
                    // matching the merchant detail page's own group-header
                    // row (`text-xs font-semibold`, `py-1.5`).
                    className={cn("h-auto py-1.5 text-center text-xs font-semibold", group.className)}
                    style={group.style}
                  >
                    {group.label}
                  </TableHead>
                ))}
              </TableRow>
            )}
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.key} className={column.className} style={column.headerStyle}>
                  <span className="inline-flex items-center gap-1">
                    {column.sortable && onSortChange ? (
                      <button
                        type="button"
                        onClick={() => onSortChange(column.key)}
                        className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                      >
                        {column.header}
                        {sortBy === column.key ? (
                          sortOrder === "asc" ? (
                            <ChevronUp className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5" />
                          )
                        ) : (
                          <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />
                        )}
                      </button>
                    ) : (
                      column.header
                    )}
                    {column.filter && <ColumnFilterButton header={column.header} filter={column.filter} />}
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {showSkeleton &&
              Array.from({ length: pageSize }).map((_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`}>
                  {columns.map((column) => (
                    <TableCell key={column.key} className={column.className}>
                      <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {showError && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-10 text-center text-sm text-muted-foreground"
                >
                  {error}
                </TableCell>
              </TableRow>
            )}

            {showEmpty && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-10 text-center text-sm text-muted-foreground"
                >
                  {searching && items.length > 0 ? `No rows on this page match "${query.trim()}".` : emptyMessage}
                </TableCell>
              </TableRow>
            )}

            {showRows &&
              visibleItems.map((row) => (
                <TableRow
                  key={getRowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={onRowClick ? "cursor-pointer" : undefined}
                >
                  {columns.map((column) => (
                    <TableCell key={column.key} className={column.className} style={column.cellStyle}>
                      {column.cell(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <label htmlFor={pageSizeId}>Rows per page</label>
          <select
            id={pageSizeId}
            className={cn(
              "h-8 rounded-md border border-input bg-transparent px-2 text-sm shadow-sm",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            )}
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              <ChevronLeft />
              Prev
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page * pageSize >= total}
              onClick={() => onPageChange(page + 1)}
            >
              Next
              <ChevronRight />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
